import { useEffect, useState } from 'react'
import { useRecoilValue } from 'recoil'
import type { StoreInfo } from '@abcpay/shared'
import { getDefaultReceiptStoreInfo } from '../utils/receiptSettings'
import { branchesAtom, selectedBranchAtom } from '../state/atoms'
import { receiptSettings } from '../utils/settingsManager'

const fields: { key: keyof StoreInfo; label: string; type: string }[] = [
  { key: 'name', label: '상호명', type: 'text' },
  { key: 'address', label: '주소', type: 'text' },
  { key: 'phone', label: '전화번호', type: 'tel' },
  { key: 'businessNumber', label: '사업자등록번호', type: 'text' },
]

export default function ReceiptSettingsTab() {
  const branches = useRecoilValue(branchesAtom)
  const selectedBranch = useRecoilValue(selectedBranchAtom)
  const [branchNid, setBranchNid] = useState('')
  const branch = branches.find((item) => item.nid === (branchNid || selectedBranch?.nid || branches[0]?.nid))
  const [info, setInfo] = useState<StoreInfo | null>(null)
  const [loadedFor, setLoadedFor] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    let cancelled = false
    setInfo(null)
    setLoadedFor('')
    setError('')
    setMessage('')
    if (branch) {
      receiptSettings.load(branch).then((value) => {
        if (cancelled) return
        setInfo(value)
        setLoadedFor(branch.nid)
      }).catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : '영수증 정보를 불러오지 못했습니다.')
      })
    }
    return () => { cancelled = true }
  }, [branch?.nid, branch?.name])

  if (!branch) {
    return <p className="py-6 text-sm text-gray-600">로그인 후 지점을 선택하면 영수증 정보를 설정할 수 있습니다.</p>
  }

  const ready = loadedFor === branch.nid && info !== null
  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!ready || !info || saving) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const result = await receiptSettings.save(branch, info)
      if (!result.success) throw new Error(result.message || '영수증 정보 저장에 실패했습니다.')
      setMessage(`${branch.name} 영수증 정보를 이 PC에 저장했습니다.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : '영수증 정보 저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    if (!ready || saving) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const result = await receiptSettings.reset(branch)
      if (!result.success) throw new Error(result.message || '기본값 복원에 실패했습니다.')
      setInfo(getDefaultReceiptStoreInfo(branch.name))
      setMessage(`${branch.name} 영수증 정보를 기본값으로 복원했습니다.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : '기본값 복원에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSave} className="space-y-4">
      <div>
        <label htmlFor="receipt-branch" className="block text-sm font-medium text-gray-700 mb-2">설정할 지점</label>
        <select
          id="receipt-branch"
          value={branch.nid}
          onChange={(event) => setBranchNid(event.target.value)}
          disabled={saving}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
        >
          {branches.map((item) => <option key={item.nid} value={item.nid}>{item.name}</option>)}
        </select>
      </div>
      <p className="text-sm text-gray-600">
        충무로·광장시장 기본값을 제공합니다. 변경한 정보는 이 PC의 해당 지점에만 저장됩니다.
        주소·전화번호·사업자등록번호를 비워 두면 영수증에서 생략됩니다.
      </p>
      {fields.map((field) => (
        <div key={field.key}>
          <label htmlFor={`receipt-${field.key}`} className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
          <input
            id={`receipt-${field.key}`}
            type={field.type}
            value={info?.[field.key] ?? ''}
            required={field.key === 'name'}
            disabled={!ready || saving}
            onChange={(event) => {
              setInfo((previous) => previous && { ...previous, [field.key]: event.target.value })
              setMessage('')
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          />
        </div>
      ))}
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      {message && <p role="status" className="text-sm text-green-700">{message}</p>}
      <div className="flex gap-3">
        <button type="button" onClick={handleReset} disabled={!ready || saving} className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
          기본값 복원
        </button>
        <button type="submit" disabled={!ready || saving} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50">
          {saving ? '저장 중...' : '영수증 정보 저장'}
        </button>
      </div>
    </form>
  )
}
